import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, LeafIcon, FlameIcon, DropletIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Land Use & Forestry
{
  id: 'lu1',
  title: 'Agricultural Land Use Permit',
  description: 'Apply for permits to use land for agricultural purposes',
  category: 'Land Use & Forestry',
  estimatedTime: '15-20 business days',
  fee: '$200.00'
}, {
  id: 'lu2',
  title: 'Tree Removal / Forestry Permit',
  description: 'Request permission for tree removal under local bylaws',
  category: 'Land Use & Forestry',
  estimatedTime: '10-15 business days',
  fee: '$150.00'
},
// Livestock Management
{
  id: 'lm1',
  title: 'Livestock Keeping Permit',
  description: 'Apply for permission to keep livestock in urban/suburban areas',
  category: 'Livestock Management',
  estimatedTime: '10-12 business days',
  fee: '$175.00'
},
// Resource Access
{
  id: 'ra1',
  title: 'Seasonal Burn Permit',
  description: 'Obtain permission for seasonal agricultural burning activities',
  category: 'Resource Access',
  estimatedTime: '5-7 business days',
  fee: '$75.00'
}, {
  id: 'ra2',
  title: 'Local Water Source Access Permit',
  description: 'Request access to local water sources for agricultural use',
  category: 'Resource Access',
  estimatedTime: '7-10 business days',
  fee: '$125.00'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Land Use & Forestry':
      return <div className="h-6 w-6 text-green-600" data-id="element-3175" />;
    case 'Livestock Management':
      return <LeafIcon className="h-6 w-6 text-blue-600" data-id="element-3176" />;
    case 'Resource Access':
      return <DropletIcon className="h-6 w-6 text-orange-600" data-id="element-3177" />;
    default:
      return <div className="h-6 w-6 text-gray-600" data-id="element-3178" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3179">
      <div className="p-5 flex flex-col h-full" data-id="element-3180">
        <div className="flex-1" data-id="element-3181">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3182">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3183">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3184">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3185">
            <div className="flex items-center" data-id="element-3186">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3187">
                ⏱
              </span>
              <span data-id="element-3188">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3189">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3190">
                💰
              </span>
              <span data-id="element-3191">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3192">
            Apply Now
            <span className="ml-1" data-id="element-3193">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3194">
      <div className="flex items-center mb-4" data-id="element-3195">
        <CategoryIcon category={category} data-id="element-3196" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3197">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3198">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3199" />)}
      </div>
    </div>;
};
export const AgricultureResourcesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3201">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3202">
          <a href="/" className="hover:text-gray-700" data-id="element-3203">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3204" />
          <span className="text-gray-900" data-id="element-3205">Agriculture & Resources</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3206">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3207">
            Agriculture & Resources
          </h1>
          <p className="text-gray-600" data-id="element-3208">
            Access permits and services for agricultural land use, livestock
            management, and natural resources
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3209">
          <div className="max-w-md relative" data-id="element-3210">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3211">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3212" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3213" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-3214" />)}
      </div>
    </div>;
};